import { cn } from './utils';

/** Where the brand marks link to. One constant so it can never drift apart. */
export const OMEECRON_URL = 'https://omeecron.com';

export interface OmeecronLogoProps {
  /** Rendered pixel size of the square mark. */
  size?: number;
  className?: string;
}

/**
 * The Omeecron mark. Served from /omeecron-logo.png rather than inlined so the
 * browser caches it once across the whole app instead of re-parsing a large
 * data URI on every route that shows it.
 */
export function OmeecronLogo({ size = 24, className }: OmeecronLogoProps) {
  return (
    <img
      src="/omeecron-logo.png"
      width={size}
      height={size}
      alt="Omeecron"
      loading="lazy"
      decoding="async"
      className={cn('shrink-0 object-contain', className)}
      style={{ width: size, height: size }}
    />
  );
}

export interface OmeecronFooterProps {
  className?: string;
  /** Compact variant for tight spaces such as the sidebar foot. */
  compact?: boolean;
}

/**
 * "Powered by Omeecron" — the attribution line that carries the mark and links
 * back to omeecron.com.
 *
 * `rel="noreferrer"` alongside `noopener` because this opens a third-party tab
 * from an authenticated ERP; there is no reason to leak the tenant's internal
 * URL as a referrer.
 */
export function OmeecronFooter({ className, compact = false }: OmeecronFooterProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 text-muted-foreground',
        compact ? 'text-[11px]' : 'text-xs',
        className,
      )}
    >
      <span>Powered by</span>
      <a
        href={OMEECRON_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center gap-1.5 font-medium text-foreground/80',
          'rounded-sm transition-colors hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        )}
      >
        <OmeecronLogo size={compact ? 14 : 16} />
        <span>Omeecron</span>
      </a>
    </div>
  );
}
